/* eslint-disable */
/**
 * ============================================================================
 * ZiniPlaybook — Shared Wireframe Primitives
 * ============================================================================
 *
 * Tiny, presentation-only building blocks used by every UX Wizard and
 * Prototype across all ZiniZ apps. They are intentionally "dumb": they take
 * props, render markup with tokens from `tokens.js`, and never own state.
 *
 * ----------------------------------------------------------------------------
 * AGENT RULES  (Claude / Cursor / Codex / Antigravity — read before editing)
 * ----------------------------------------------------------------------------
 *   ✅ DO   import these primitives instead of re-defining Row/Box/MockBtn…
 *           in every wizard file
 *   ✅ DO   pass the parent's `dark` boolean through to <WF dark={dark} />
 *   ✅ DO   keep wireframes non-interactive — these primitives use
 *           `cursor: default` on purpose (they are mock UI, not real UI)
 *   ✅ DO   reach for ACCENTS / T tokens when a primitive needs a custom color
 *   ✅ DO   use <Section>, <CardBlock>, <FormBlock>, <DataBlock> for structured
 *           screen layout definitions — they carry data-zh-* attributes for
 *           automatic extraction into ziniz-bundle.json
 *
 *   ❌ DON'T add real behavior (routing, network, form state) to these files
 *   ❌ DON'T copy-paste these into wizard files — always import from this path
 *   ❌ DON'T introduce new dependencies; these must stay pure React + tokens
 *
 * ----------------------------------------------------------------------------
 * EXPORTS
 * ----------------------------------------------------------------------------
 *   Layout:    <Row />, <Box />  — prefer gap/mb from layoutTokens.js in new code
 *   Tokens:    import { SPACING, TYPO, pagePadForViewport, WIREFRAME_CANVAS_HEIGHT, canvasHeightForViewport } from "./layoutTokens"
 *              — page padding/heights from layoutTokens, not inline magic numbers in flow preview
 *   Mock UI:   <MockBtn />, <MockField />, <MockBadge />
 *   Frame:     <WF />            (browser-chrome wrapper)
 *   Layout blocks: <Section />, <CardBlock />, <FormBlock />, <DataBlock />,
 *                  <TextBlock />, <ActionBlock />, <OverlayBlock />,
 *                  <ProgressBlock />, <TabBlock />, <UploadBlock />
 *   Renderer:  renderLayout(schema, ctx)  — walks a ScreenLayout schema → JSX
 *   Constants: BTN_TYPES, BADGE_TYPES
 *
 * ----------------------------------------------------------------------------
 * MINIMAL USAGE
 * ----------------------------------------------------------------------------
 *   import {
 *     Row, Box, MockBtn, MockField, MockBadge, WF,
 *   } from "../../shared/WireframePrimitives";
 *
 *   <WF dark={dark} viewport="desktop" domain="zinihub.ziniz.online">
 *     <Row gap={8} mb={12}>
 *       <MockBadge label="Live" type="active" />
 *       <MockBtn label="Save" type="primary" />
 *     </Row>
 *     <MockField label="Store name" val="Hub 22" req />
 *     <Box w="60%" h={10} />
 *   </WF>
 *
 * ============================================================================
 */

import { T, useTheme, ACCENTS } from "../tokens";
import { TYPO, SPACING } from "./layoutTokens";

// ── Layout helpers ────────────────────────────────────────────────────────────

/**
 * Horizontal flex row.
 *
 * @param {object}  props
 * @param {React.ReactNode} props.children
 * @param {number} [props.gap=8]            - gap between children (px)
 * @param {number} [props.mb=0]             - bottom margin (px)
 * @param {"center"|"flex-start"|"flex-end"|"baseline"|"stretch"} [props.align="center"]
 * @param {boolean} [props.wrap=false]      - allow flex-wrap
 * @param {object} [props.style]            - escape hatch for extra styles
 */
export const Row = ({ children, gap = 8, mb = 0, align = "center", wrap = false, style = {} }) => (
  <div
    style={{
      display: "flex",
      gap,
      alignItems: align,
      marginBottom: mb,
      flexWrap: wrap ? "wrap" : "nowrap",
      ...style,
    }}
  >
    {children}
  </div>
);

/**
 * Generic rectangular placeholder — used to mock text lines, image slots,
 * dividers, progress fills, etc.
 *
 * @param {object}  props
 * @param {string|number} [props.w="100%"]  - width
 * @param {number} [props.h=12]             - height (px)
 * @param {number} [props.r=6]              - border radius (px)
 * @param {string} [props.bg]               - background; defaults to neutral grey
 * @param {object} [props.style]            - escape hatch
 */
export const Box = ({ w = "100%", h = 12, r = 6, bg, style = {} }) => (
  <div
    style={{
      width: w,
      height: h,
      borderRadius: r,
      background: bg || "rgba(128,128,128,0.12)",
      flexShrink: 0,
      ...style,
    }}
  />
);

// ── Mock buttons ──────────────────────────────────────────────────────────────

/** Valid `type` values for <MockBtn />. */
export const BTN_TYPES = Object.freeze(["primary", "outline", "danger", "ghost"]);

const BTN_STYLES = {
  primary: { background: T.brand,       color: "#fff",                   border: "none" },
  outline: { background: "transparent", color: "rgba(128,128,128,0.65)", border: "1px solid rgba(128,128,128,0.2)" },
  danger:  { background: "transparent", color: T.red,                    border: `1px solid ${T.red}44` },
  ghost:   { background: T.brandSoft,   color: T.brand,                  border: `1px solid ${T.brandBorder}` },
};

/**
 * Non-interactive mock button.
 *
 * @param {object}  props
 * @param {string}  props.label
 * @param {"primary"|"outline"|"danger"|"ghost"} [props.type="outline"]
 * @param {string|number} [props.w]        - fixed width
 * @param {boolean} [props.small=false]    - 26px (vs 34px) height
 * @param {object} [props.style]
 */
export const MockBtn = ({ label, type = "outline", w, small, style = {} }) => (
  <div
    style={{
      height: small ? 26 : 34,
      padding: "0 12px",
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: small ? 10 : 12,
      fontWeight: type === "primary" ? 500 : 400,
      width: w,
      flexShrink: 0,
      cursor: "default",
      ...(BTN_STYLES[type] || BTN_STYLES.outline),
      ...style,
    }}
  >
    {label}
  </div>
);

// ── Mock form field ───────────────────────────────────────────────────────────

/**
 * Non-interactive mock input row (label + box + optional hint).
 *
 * @param {object}  props
 * @param {string}  props.label
 * @param {string} [props.val]              - placeholder/sample value
 * @param {string} [props.hint]             - helper text under the field
 * @param {boolean} [props.req=false]       - prepends red asterisk
 * @param {string|number} [props.w="100%"]
 */
export const MockField = ({ label, val, hint, req, w = "100%" }) => (
  <div style={{ width: w, marginBottom: 10 }}>
    <div style={{ fontSize: 10, color: "rgba(128,128,128,0.6)", marginBottom: 3 }}>
      {req && <span style={{ color: T.red }}>* </span>}
      {label}
    </div>
    <div
      style={{
        height: 34,
        background: "rgba(128,128,128,0.07)",
        border: "1px solid rgba(128,128,128,0.15)",
        borderRadius: 7,
        padding: "0 10px",
        display: "flex",
        alignItems: "center",
      }}
    >
      {val && <span style={{ fontSize: 11, color: "rgba(128,128,128,0.65)" }}>{val}</span>}
    </div>
    {hint && <div style={{ fontSize: 9, color: "rgba(128,128,128,0.4)", marginTop: 2 }}>{hint}</div>}
  </div>
);

// ── Mock badge ────────────────────────────────────────────────────────────────

const BADGE_STYLES = {
  // Order / lifecycle states
  active:       { bg: ACCENTS.green.soft,  t: ACCENTS.green.solid  },
  live:         { bg: ACCENTS.green.soft,  t: ACCENTS.green.solid  },
  open:         { bg: ACCENTS.green.soft,  t: ACCENTS.green.solid  },
  closed:       { bg: ACCENTS.red.soft,    t: ACCENTS.red.solid    },
  placed:       { bg: ACCENTS.amber.soft,  t: ACCENTS.amber.solid  },
  confirmed:    { bg: "rgba(59,130,246,0.15)", t: "#60a5fa"        },
  cancelled:    { bg: ACCENTS.red.soft,    t: ACCENTS.red.solid    },
  delivered:    { bg: ACCENTS.green.soft,  t: ACCENTS.green.solid  },
  processing:   { bg: ACCENTS.brand.soft,  t: ACCENTS.brand.solid  },
  draft:        { bg: "rgba(128,128,128,0.12)", t: "rgba(128,128,128,0.55)" },
  public:       { bg: ACCENTS.green.soft,  t: ACCENTS.green.solid  },
  private:      { bg: ACCENTS.amber.soft,  t: ACCENTS.amber.solid  },

  // Subscription plans
  expiring:     { bg: ACCENTS.amber.soft,  t: ACCENTS.amber.solid  },
  expired:      { bg: ACCENTS.red.soft,    t: ACCENTS.red.solid    },
  trial:        { bg: ACCENTS.amber.soft,  t: ACCENTS.amber.solid  },
  starter:      { bg: ACCENTS.cyan.soft,   t: ACCENTS.cyan.solid   },
  professional: { bg: ACCENTS.brand.soft,  t: ACCENTS.brand.solid  },
  enterprise:   { bg: ACCENTS.violet.soft, t: ACCENTS.violet.solid },

  // Category / type
  product:      { bg: ACCENTS.brand.soft,  t: ACCENTS.brand.solid  },
  service:      { bg: ACCENTS.teal.soft,   t: ACCENTS.teal.solid   },
  delivery:     { bg: ACCENTS.cyan.soft,   t: ACCENTS.cyan.solid   },
  pickup:       { bg: ACCENTS.amber.soft,  t: ACCENTS.amber.solid  },

  // Fallback
  neutral:      { bg: "rgba(128,128,128,0.10)", t: "rgba(128,128,128,0.55)" },
};

/** Valid `type` values for <MockBadge />. */
export const BADGE_TYPES = Object.freeze(Object.keys(BADGE_STYLES));

/**
 * Non-interactive mock badge / pill.
 *
 * Pass either a known `type` (see BADGE_TYPES) for a curated colour pair, or
 * pass `color` + `bg` directly to override.
 *
 * @param {object}  props
 * @param {string}  props.label
 * @param {string} [props.type="neutral"]   - curated style key
 * @param {string} [props.color]            - override foreground
 * @param {string} [props.bg]               - override background
 * @param {object} [props.style]
 */
export const MockBadge = ({ label, type = "neutral", color, bg, style = {} }) => {
  const c = BADGE_STYLES[type?.toLowerCase()] || BADGE_STYLES.neutral;
  const fg = color || c.t;
  const background = bg || c.bg;
  return (
    <span
      style={{
        background,
        color: fg,
        borderRadius: 5,
        padding: "2px 7px",
        fontSize: 10,
        fontWeight: 500,
        flexShrink: 0,
        border: `1px solid ${fg}22`,
        ...style,
      }}
    >
      {label}
    </span>
  );
};

// ── Browser frame wrapper ─────────────────────────────────────────────────────

/**
 * Faux browser window around wireframe content. Renders traffic-light dots,
 * a URL bar, and a viewport label. Switches widths for the "mobile" viewport.
 *
 * @param {object}  props
 * @param {boolean} props.dark
 * @param {"desktop"|"mobile"} [props.viewport="desktop"]
 * @param {boolean} [props.slim=false]     - tighter content padding
 * @param {boolean} [props.noPad=false]    - remove content padding entirely
 * @param {string} [props.domain]          - URL bar text (default: zinihub.ziniz.online)
 * @param {React.ReactNode} props.children
 */
export function WF({ dark, viewport = "desktop", slim = false, noPad = false, domain, children }) {
  const th = useTheme(dark);
  const w = viewport === "mobile" ? "375px" : "100%";
  const url = domain || "zinihub.ziniz.online";
  return (
    <div
      style={{
        width: `min(100%,${w})`,
        background: dark ? T.dElevated : T.lMuted,
        borderRadius: T.rlg,
        border: `1px solid ${th.border}`,
        overflow: "hidden",
        transition: `width ${T.slow} ${T.ease}`,
      }}
    >
      {/* Chrome bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          padding: "6px 10px",
          background: dark ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.04)",
          borderBottom: `1px solid ${th.border}`,
        }}
      >
        {["#ff5f57", "#ffbd2e", "#28c840"].map((c) => (
          <div key={c} style={{ width: 6, height: 6, borderRadius: "50%", background: c }} />
        ))}
        <div
          style={{
            flex: 1,
            height: 14,
            background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
            borderRadius: 3,
            margin: "0 5px",
            paddingLeft: 7,
            display: "flex",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: 8,
              color: dark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
              fontFamily: "monospace",
            }}
          >
            {url}
          </span>
        </div>
        <span
          style={{
            fontSize: 8,
            color: dark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)",
            background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
            borderRadius: 3,
            padding: "1px 5px",
          }}
        >
          {viewport}
        </span>
      </div>
      {/* Content */}
      <div style={{ padding: noPad ? 0 : slim ? "10px 12px" : "14px 16px" }}>{children}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Screen Layout Primitives (Phase B)
// Each carries data-zh-id / data-zh-type / data-zh-props for DOM extraction.
// ═══════════════════════════════════════════════════════════════════════════════

function zh(node, id, type, props) {
  const data = { ...props };
  return { "data-zh-id": id, "data-zh-type": type, "data-zh-props": JSON.stringify(data), ...node };
}

/**
 * @param {object}  props
 * @param {string}  props.id      — unique block id within the screen
 * @param {"row"|"column"|"row-reverse"|"column-reverse"|"grid"} [props.direction="column"]
 * @param {number} [props.gap]    — gap in px
 * @param {number|[number,number]} [props.padding]
 * @param {string|number} [props.width]
 * @param {object} [props.grid]   — { template, minColumnWidth, columnCount, columns }
 * @param {string} [props.align]
 * @param {string} [props.justify]
 * @param {string} [props.surface]
 * @param {boolean} [props.scrollable]
 */
export const Section = ({
  id, direction = "column", gap, padding, width, grid,
  align, justify, surface, scrollable,
  children, style = {}, ...rest
}) => {
  const th = useTheme(rest.dark);
  const isGrid = direction === "grid";
  let gridStyle = {};
  if (isGrid && grid) {
    if (grid.template === "auto-fill") {
      gridStyle = { display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${grid.minColumnWidth || 140}px, 1fr))`, gap: grid.rowGap ?? gap ?? 8 };
    } else if (grid.template === "equal") {
      gridStyle = { display: "grid", gridTemplateColumns: `repeat(${grid.columnCount || 2}, 1fr)`, gap: grid.rowGap ?? gap ?? 8 };
    } else if (grid.template === "fixed" && grid.columns) {
      const cols = grid.columns.map(c => (typeof c.width === "number" ? `${c.width}px` : c.width)).join(" ");
      gridStyle = { display: "grid", gridTemplateColumns: cols, gap: grid.rowGap ?? gap ?? 8 };
    }
    if (grid.gap) gridStyle.gap = grid.gap;
  }
  const pad = padding != null ? (Array.isArray(padding) ? `${padding[0]}px ${padding[1]}px` : `${padding}px`) : undefined;
  const bg = surface ? (surface === "brand" ? T.brandSoft : surface === "brandSoft" ? T.brandSoft : th[surface] || th.canvas) : undefined;

  return zh(
    <div
      style={{
        display: isGrid ? undefined : "flex",
        flexDirection: isGrid ? undefined : direction,
        gap: isGrid ? undefined : gap,
        alignItems: align ? { start: "flex-start", center: "center", end: "flex-end", stretch: "stretch" }[align] : undefined,
        justifyContent: justify ? { start: "flex-start", center: "center", end: "flex-end", between: "space-between", around: "space-around" }[justify] : undefined,
        padding: pad,
        width: typeof width === "number" ? width : width,
        background: bg,
        overflow: scrollable ? "auto" : undefined,
        flex: 1,
        cursor: "default",
        ...gridStyle,
        ...style,
      }}
    >
      {children}
    </div>,
    id, "section", { direction, gap, padding, width, grid: grid ? JSON.stringify(grid) : undefined, surface, scrollable }
  );
};

/**
 * @param {object}  props
 * @param {string}  props.id
 * @param {string} [props.title]
 * @param {object} [props.titleAction]  — { label, action }
 * @param {number} [props.padding]
 * @param {number} [props.width]
 * @param {"flat"|"raised"|"outlined"} [props.elevation="outlined"]
 * @param {string} [props.surface]
 */
export const CardBlock = ({
  id, title, titleAction, padding, width, elevation = "outlined", surface,
  children, style = {}, ...rest
}) => {
  const th = useTheme(rest.dark);
  const bg = surface ? th[surface] || th.surface : th.surface;
  const borderColor = elevation === "outlined" ? th.border : "transparent";
  const boxShadow = elevation === "raised" ? `0 2px 8px ${th.borderMd}22` : "none";

  return zh(
    <div style={{
      background: bg,
      borderRadius: T.rmd,
      border: `1px solid ${borderColor}`,
      boxShadow,
      padding: padding != null ? padding : 16,
      width: typeof width === "number" ? width : width,
      cursor: "default",
      ...style,
    }}>
      {title && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: th.text }}>{title}</span>
          {titleAction && (
            <span style={{ fontSize: 10, color: T.brand, cursor: "default" }}>{titleAction.label}</span>
          )}
        </div>
      )}
      {children}
    </div>,
    id, "card", { padding, width, elevation, surface }
  );
};

/**
 * @param {object} props
 * @param {string} props.id
 * @param {Array}  props.fields     — FormField[]
 * @param {object} props.submit     — { label, variant, width }
 * @param {object} [props.linkAfterSubmit] — { label, action }
 * @param {"vertical"|"horizontal"} [props.layout="vertical"]
 */
export const FormBlock = ({
  id, fields = [], submit, linkAfterSubmit, layout = "vertical", style = {}, ...rest
}) => {
  const isMobile = rest.viewport === "mobile";
  return zh(
    <div style={{ width: "100%", cursor: "default", ...style }}>
      {fields.map((f) => (
        <MockField
          key={f.id}
          label={f.label}
          req={f.required}
          hint={f.hint}
          w={isMobile ? "100%" : f.type === "phone" ? 260 : "100%"}
        />
      ))}
      {submit && (
        <div style={{ marginTop: fields.length ? 16 : 0 }}>
          <MockBtn label={submit.label} type={submit.variant || "primary"} w={submit.width || "100%"} />
        </div>
      )}
      {linkAfterSubmit && (
        <div style={{ marginTop: 12, textAlign: "center" }}>
          <span style={{ fontSize: 10, color: T.brand, cursor: "default" }}>{linkAfterSubmit.label}</span>
        </div>
      )}
    </div>,
    id, "form", { fieldCount: fields.length, hasSubmit: !!submit, layout }
  );
};

/**
 * @param {object} props
 * @param {string} props.id
 * @param {"table"|"list"|"card-grid"|"stats"|"key-value"} props.type
 * @param {Array}  [props.columns]   — ColumnDef[]
 * @param {Array}  [props.items]
 * @param {string} [props.emptyMessage]
 * @param {Array}  [props.stats]
 * @param {string} [props.primaryAction]
 * @param {object} [props.rowAction]
 */
export const DataBlock = ({
  id, type = "table", columns = [], items = [], emptyMessage, stats,
  primaryAction, rowAction, style = {}, ...rest
}) => {
  const th = useTheme(rest.dark);
  const isDark = rest.dark;

  if (type === "stats" && stats) {
    return zh(
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(stats.length, 4)}, 1fr)`, gap: 12, cursor: "default", ...style }}>
        {stats.map((s, i) => (
          <div key={i} style={{
            background: th.surface,
            borderRadius: T.rmd,
            border: `1px solid ${th.border}`,
            padding: "14px 16px",
          }}>
            <div style={{ fontSize: 9, color: th.textMuted, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: th.text }}>{s.value}</div>
            {s.change && (
              <div style={{ fontSize: 9, color: s.trend === "positive" ? T.green : s.trend === "negative" ? T.red : th.textMuted, marginTop: 2 }}>
                {s.change.direction === "up" ? "↑" : s.change.direction === "down" ? "↓" : "→"} {s.change.value}
              </div>
            )}
          </div>
        ))}
      </div>,
      id, "stats", { statCount: stats.length }
    );
  }

  if (type === "list") {
    return zh(
      <div style={{ cursor: "default", ...style }}>
        {items.length === 0 && emptyMessage ? (
          <div style={{ padding: 24, textAlign: "center", fontSize: 11, color: th.textMuted }}>{emptyMessage}</div>
        ) : (
          items.map((item, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 0", borderBottom: i < items.length - 1 ? `1px solid ${th.border}` : "none",
              cursor: "default",
            }}>
              <div style={{ fontSize: 11, color: th.text }}>{item.label || item.name || `Item ${i + 1}`}</div>
              <div style={{ fontSize: 11, color: th.textSec }}>{item.value || item.subtitle || ""}</div>
            </div>
          ))
        )}
        {primaryAction && (
          <div style={{ marginTop: 8 }}>
            <MockBtn label={primaryAction} type="ghost" w="100%" />
          </div>
        )}
      </div>,
      id, "list", { itemCount: items.length, hasPrimaryAction: !!primaryAction }
    );
  }

  if (type === "key-value") {
    return zh(
      <div style={{ cursor: "default", ...style }}>
        {items.map((item, i) => (
          <div key={i} style={{
            display: "flex", padding: "8px 0",
            borderBottom: i < items.length - 1 ? `1px solid ${th.border}` : "none",
            cursor: "default",
          }}>
            <div style={{ width: 90, fontSize: 10, color: th.textMuted, flexShrink: 0 }}>{item.label}</div>
            <div style={{ fontSize: 11, color: th.text }}>{item.value}</div>
          </div>
        ))}
      </div>,
      id, "key-value", { itemCount: items.length }
    );
  }

  return zh(
    <div style={{ cursor: "default", ...style }}>
      {columns.length > 0 && (
        <div style={{ display: "flex", borderBottom: `1px solid ${th.borderMd}`, paddingBottom: 6, marginBottom: 6 }}>
          {columns.map((col, i) => (
            <div key={i} style={{
              flex: col.width === "1fr" ? 1 : undefined,
              width: typeof col.width === "number" ? col.width : col.width === "1fr" ? undefined : col.width,
              fontSize: 9, fontWeight: 600, color: th.textMuted,
              textTransform: "uppercase", letterSpacing: "0.5px",
            }}>{col.label || ""}</div>
          ))}
        </div>
      )}
      {items.length === 0 && emptyMessage ? (
        <div style={{ padding: 24, textAlign: "center", fontSize: 11, color: th.textMuted }}>{emptyMessage}</div>
      ) : (
        items.map((item, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", padding: "7px 0",
            borderBottom: i < items.length - 1 ? `1px solid ${th.border}` : "none",
            cursor: "default",
          }}>
            {columns.map((col, ci) => {
              const val = item[col.key ?? col.label?.toLowerCase()] || "";
              return (
                <div key={ci} style={{
                  flex: col.width === "1fr" ? 1 : undefined,
                  width: typeof col.width === "number" ? col.width : col.width === "1fr" ? undefined : col.width,
                  fontSize: 10, color: typeof val === "object" ? undefined : th.text,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {typeof val === "object" && val.badge ? (
                    <MockBadge label={val.badge} type={val.type || "neutral"} />
                  ) : val}
                </div>
              );
            })}
          </div>
        ))
      )}
      {primaryAction && (
        <div style={{ marginTop: 8 }}>
          <MockBtn label={primaryAction} type="ghost" w="100%" />
        </div>
      )}
    </div>,
    id, type, { colCount: columns.length, itemCount: items.length }
  );
};

/**
 * @param {object} props
 * @param {string} props.id
 * @param {string} props.text
 * @param {"hero"|"pageTitle"|"sectionTitle"|"cardTitle"|"body"|"caption"|"muted"} [props.variant="body"]
 * @param {"text"|"textSec"|"textMuted"|"brand"|"danger"} [props.color]
 * @param {"left"|"center"|"right"} [props.align]
 */
export const TextBlock = ({ id, text, variant = "body", color, align, style = {}, ...rest }) => {
  const th = useTheme(rest.dark);
  const t = TYPO[variant] || TYPO.body;
  const palette = { text: th.text, textSec: th.textSec, textMuted: th.textMuted, brand: T.brand, danger: T.red };
  return zh(
    <div style={{
      fontSize: t.fontSize,
      fontWeight: t.fontWeight,
      lineHeight: t.lineHeight,
      color: color ? (palette[color] || th.text) : variant === "muted" ? th.textMuted : th.text,
      textAlign: align || "left",
      cursor: "default",
      ...style,
    }}>
      {text}
    </div>,
    id, "text", { variant, color }
  );
};

/**
 * @param {object} props
 * @param {string} props.id
 * @param {Array}  props.buttons     — ActionButton[]
 * @param {"row"|"column"} [props.direction="row"]
 * @param {"start"|"center"|"end"|"between"} [props.justify]
 * @param {number} [props.gap]
 */
export const ActionBlock = ({ id, buttons = [], direction = "row", justify, gap = 8, style = {}, ...rest }) => {
  return zh(
    <div style={{
      display: "flex", flexDirection: direction, gap,
      justifyContent: justify ? { start: "flex-start", center: "center", end: "flex-end", between: "space-between" }[justify] : undefined,
      cursor: "default", ...style,
    }}>
      {buttons.map((btn, i) => (
        <MockBtn key={i} label={btn.label} type={btn.variant || "outline"} w={btn.width} />
      ))}
    </div>,
    id, "actions", { buttonCount: buttons.length, direction, justify }
  );
};

/**
 * @param {object} props
 * @param {string} props.id
 * @param {"drawer-right"|"slider-left"|"modal-centered"} [props.variant="drawer-right"]
 * @param {number} [props.width]
 * @param {boolean} [props.dimBackground]
 */
export const OverlayBlock = ({ id, variant = "drawer-right", width, dimBackground = true, children, style = {}, ...rest }) => {
  const th = useTheme(rest.dark);
  const isDrawer = variant === "drawer-right";

  return zh(
    <div style={{
      position: "absolute", inset: 0, display: "flex",
      cursor: "default", ...style,
    }}>
      {dimBackground && (
        <div style={{ flex: 1, background: "rgba(0,0,0,0.3)" }} />
      )}
      <div style={{
        width: width || (isDrawer ? 240 : 320),
        flexShrink: 0, background: th.canvas, borderLeft: isDrawer ? `1px solid ${th.border}` : undefined,
        display: "flex", flexDirection: "column", cursor: "default",
        ...(variant === "modal-centered" ? { margin: "auto", borderRadius: T.rmd, border: `1px solid ${th.border}`, boxShadow: `0 4px 16px ${th.borderMd}44` } : {}),
      }}>
        {children}
      </div>
    </div>,
    id, "overlay", { variant, width, dimBackground }
  );
};

/**
 * @param {object} props
 * @param {string} props.id
 * @param {Array}  props.steps     — { label, state }[]
 * @param {number} props.activeStep
 */
export const ProgressBlock = ({ id, steps = [], activeStep = 0, style = {}, ...rest }) => {
  const th = useTheme(rest.dark);
  return zh(
    <div style={{ display: "flex", alignItems: "center", gap: 0, cursor: "default", ...style }}>
      {steps.map((step, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", flex: 1 }}>
          <div style={{
            width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
            background: step.state === "completed" ? T.brand : step.state === "current" ? T.brandSoft : "transparent",
            border: `2px solid ${step.state === "completed" ? T.brand : step.state === "current" ? T.brand : th.borderMd}`,
            fontSize: 9, fontWeight: 600, color: step.state === "completed" ? "#fff" : step.state === "current" ? T.brand : th.textMuted,
          }}>
            {step.state === "completed" ? "✓" : i + 1}
          </div>
          <span style={{
            fontSize: 9, marginLeft: 6, color: step.state === "current" ? th.text : th.textMuted,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{step.label}</span>
          {i < steps.length - 1 && (
            <div style={{ flex: 1, height: 1, background: step.state === "completed" ? T.brand : th.borderMd, margin: "0 8px" }} />
          )}
        </div>
      ))}
    </div>,
    id, "progress", { stepCount: steps.length, activeStep }
  );
};

/**
 * @param {object} props
 * @param {string} props.id
 * @param {Array}  props.tabs      — { label, id }[]
 * @param {string} props.activeTab
 */
export const TabBlock = ({ id, tabs = [], activeTab, style = {}, ...rest }) => {
  const th = useTheme(rest.dark);
  return zh(
    <div style={{ display: "flex", borderBottom: `2px solid ${th.border}`, cursor: "default", ...style }}>
      {tabs.map((tab) => (
        <div key={tab.id} style={{
          padding: "8px 16px", fontSize: 11, fontWeight: tab.id === activeTab ? 600 : 400,
          color: tab.id === activeTab ? T.brand : th.textSec,
          borderBottom: tab.id === activeTab ? `2px solid ${T.brand}` : "2px solid transparent",
          marginBottom: -2, cursor: "default",
        }}>
          {tab.label}
        </div>
      ))}
    </div>,
    id, "tabs", { tabCount: tabs.length, activeTab }
  );
};

/**
 * @param {object} props
 * @param {string} props.id
 * @param {string} props.label
 * @param {string} [props.hint]
 * @param {boolean} [props.dashed]
 */
export const UploadBlock = ({ id, label, hint, dashed = true, style = {}, ...rest }) => {
  const th = useTheme(rest.dark);
  return zh(
    <div style={{
      border: `2px ${dashed ? "dashed" : "solid"} ${th.borderMd}`,
      borderRadius: T.rmd, padding: 24, textAlign: "center", cursor: "default",
      marginBottom: 12, background: th.muted, ...style,
    }}>
      <div style={{ fontSize: 22, marginBottom: 4 }}>📄</div>
      <div style={{ fontSize: 10, color: th.textSec, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 9, color: th.textMuted }}>{hint || "Click or drag to upload"}</div>
      <div style={{ marginTop: 8 }}>
        <MockBtn label="Upload" type="outline" small />
      </div>
    </div>,
    id, "upload", { label, dashed }
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Phase C — Schema Renderer
// Walks a ScreenLayout schema and produces JSX.
// ═══════════════════════════════════════════════════════════════════════════════

const BLOCK_RENDERERS = {
  section: (node, ctx) => (
    <Section key={node.id} id={node.id} direction={node.direction} gap={node.gap} padding={node.padding} width={node.width} grid={node.grid} align={node.align} justify={node.justify} surface={node.surface} scrollable={node.scrollable} dark={ctx.dark} viewport={ctx.viewport}>
      {node.children?.map((c) => renderBlock(c, ctx))}
    </Section>
  ),
  card: (node, ctx) => (
    <CardBlock key={node.id} id={node.id} title={node.title} titleAction={node.titleAction} padding={node.padding} width={node.width} elevation={node.elevation} surface={node.surface} dark={ctx.dark}>
      {node.children?.map((c) => renderBlock(c, ctx))}
    </CardBlock>
  ),
  form: (node, ctx) => (
    <FormBlock key={node.id} id={node.id} fields={node.fields} submit={node.submit} linkAfterSubmit={node.linkAfterSubmit} layout={node.layout} dark={ctx.dark} viewport={ctx.viewport} />
  ),
  table: (node, ctx) => (
    <DataBlock key={node.id} id={node.id} type="table" columns={node.columns} items={node.items} emptyMessage={node.emptyMessage} primaryAction={node.primaryAction} rowAction={node.rowAction} dark={ctx.dark} />
  ),
  list: (node, ctx) => (
    <DataBlock key={node.id} id={node.id} type="list" items={node.items} emptyMessage={node.emptyMessage} primaryAction={node.primaryAction} dark={ctx.dark} />
  ),
  "card-grid": (node, ctx) => (
    <DataBlock key={node.id} id={node.id} type="table" columns={node.columns} items={node.items} emptyMessage={node.emptyMessage} dark={ctx.dark} />
  ),
  stats: (node, ctx) => (
    <DataBlock key={node.id} id={node.id} type="stats" stats={node.stats} dark={ctx.dark} />
  ),
  "key-value": (node, ctx) => (
    <DataBlock key={node.id} id={node.id} type="key-value" items={node.items} dark={ctx.dark} />
  ),
  text: (node, ctx) => (
    <TextBlock key={node.id} id={node.id} text={node.text} variant={node.variant} color={node.color} align={node.align} dark={ctx.dark} />
  ),
  actions: (node, ctx) => (
    <ActionBlock key={node.id} id={node.id} buttons={node.buttons} direction={node.direction} justify={node.justify} gap={node.gap} dark={ctx.dark} />
  ),
  overlay: (node, ctx) => (
    <OverlayBlock key={node.id} id={node.id} variant={node.variant} width={node.width} dimBackground={node.dimBackground} dark={ctx.dark}>
      {node.children?.map((c) => renderBlock(c, ctx))}
    </OverlayBlock>
  ),
  progress: (node, ctx) => (
    <ProgressBlock key={node.id} id={node.id} steps={node.steps} activeStep={node.activeStep} dark={ctx.dark} />
  ),
  tabs: (node, ctx) => (
    <TabBlock key={node.id} id={node.id} tabs={node.tabs} activeTab={node.activeTab} dark={ctx.dark} />
  ),
  upload: (node, ctx) => (
    <UploadBlock key={node.id} id={node.id} label={node.label} hint={node.hint} dashed={node.dashed} dark={ctx.dark} />
  ),
};

function renderBlock(node, ctx) {
  if (!node || !node.type) return null;
  const renderer = BLOCK_RENDERERS[node.type];
  if (!renderer) {
    // Fallback for unknown types — render as div with id
    return <div key={node.id} data-zh-id={node.id} data-zh-type={node.type}>{node.label || node.id}</div>;
  }
  return renderer(node, ctx);
}

/**
 * Walk a ScreenLayout schema and produce JSX wireframe output.
 *
 * @param {object} schema     — ScreenLayout { shell, shellProps, regions }
 * @param {object} ctx        — { dark, viewport, slim, domain }
 * @returns {JSX.Element}
 */
export function renderLayout(schema, ctx) {
  if (!schema) return null;
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", cursor: "default" }}>
      {schema.regions?.map((r) => renderBlock(r, ctx))}
    </div>
  );
}
